const EnterPin = (el: HTMLElement): (() => void) => {
    const inputs = Array.from(el.querySelectorAll<HTMLInputElement>('.enter-pin-input input'));
    const enterPinCta = el.querySelector<HTMLElement>('.enter-pin-cta');
    const enterPinInput = el.querySelector<HTMLElement>('.enter-pin-input');

    // --- Helpers ---
    const isFilled = (input: HTMLInputElement): boolean => {
        return input.classList.contains('filled');
    };

    const focusAndPlaceCaret = (input: HTMLInputElement): void => {
        input.focus();
        setTimeout(() => {
            input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
    };

    // Always place caret at first unfilled input, or last filled if all are filled
    const focusFirstUnfilledOrLastFilled = (): void => {
        let targetIdx = 0;
        for (let i = 0; i < inputs.length; i += 1) {
            if (!isFilled(inputs[i])) {
                targetIdx = i;
                break;
            } else {
                targetIdx = i;
            }
        }
        focusAndPlaceCaret(inputs[targetIdx]);
    };

    // --- UI State ---
    const updateButtonState = (): void => {
        if (!enterPinCta) {
            return;
        }
        const allFilled = inputs.every(isFilled);
        if (allFilled) {
            enterPinCta.classList.remove('btn-disabled');
            enterPinCta.removeAttribute('aria-disabled');
            enterPinCta.removeAttribute('disabled');
        } else {
            enterPinCta.classList.add('btn-disabled');
            enterPinCta.setAttribute('aria-disabled', 'true');
            enterPinCta.setAttribute('disabled', 'true');
        }
    };

    const maskInput = (input: HTMLInputElement): void => {
        input.value = '*';
        input.classList.add('filled');
    };

    const clearInput = (input: HTMLInputElement): void => {
        input.value = '';
        input.classList.remove('filled');
        input.dataset.realValue = '';
    };

    // --- Event Handlers ---
    const handleInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        input.value = input.value.replace(/\D/g, '');
        if (input.value.length === 1) {
            input.dataset.realValue = input.value;
            maskInput(input);
            // Auto-advance
            const idx = inputs.indexOf(input);
            if (idx < inputs.length - 1) {
                focusAndPlaceCaret(inputs[idx + 1]);
            }
        } else {
            clearInput(input);
        }
        updateButtonState();
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
        const input = e.target as HTMLInputElement;
        if (e.key === 'Backspace' && !input.dataset.realValue) {
            const idx = inputs.indexOf(input);
            if (idx > 0) {
                clearInput(inputs[idx - 1]);
                focusAndPlaceCaret(inputs[idx - 1]);
            }
        }
    };

    const handleStrictBackspace = (e: KeyboardEvent): void => {
        const input = e.target as HTMLInputElement;
        if (e.key === 'Backspace') {
            const idx = inputs.indexOf(input);
            let lastFilled = -1;
            for (let i = inputs.length - 1; i >= 0; i -= 1) {
                if (isFilled(inputs[i])) {
                    lastFilled = i;
                    break;
                }
            }
            if (idx !== lastFilled) {
                e.preventDefault();
                if (lastFilled > -1) {
                    focusAndPlaceCaret(inputs[lastFilled]);
                }
            }
        }
    };

    const handleEnterKey = (e: KeyboardEvent): void => {
        if (e.key === 'Enter' && enterPinCta && !enterPinCta.classList.contains('btn-disabled')) {
            enterPinCta.click();
        }
    };

    const handleButtonClick = (): void => {
        const pin = inputs.map(input => {
            return input.dataset.realValue || '';
        }).join('');
        // Populate the hidden input for BED with the full PIN
        const hiddenInput = document.getElementById('fullPin') as HTMLInputElement | null;
        if (hiddenInput) {
            hiddenInput.value = pin;
        }
    };

    const handleFocus = (): void => {
        focusFirstUnfilledOrLastFilled();
    };

    const handleMouseUp = (): void => {
        focusFirstUnfilledOrLastFilled();
    };

    const handleContainerClick = (e: Event): void => {
        if ((e.target as HTMLElement).tagName.toLowerCase() === 'input') {
            return;
        }
        focusFirstUnfilledOrLastFilled();
    };

    // --- Event Listener Setup ---
    inputs.forEach(input => {
        input.type = 'tel';
        input.addEventListener('input', handleInput);
        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('keydown', handleStrictBackspace);
        input.addEventListener('keydown', handleEnterKey);
        input.addEventListener('focus', handleFocus);
        input.addEventListener('mouseup', handleMouseUp);
        if (input.value) {
            input.classList.add('filled');
        }
    });

    if (enterPinCta) {
        enterPinCta.addEventListener('click', handleButtonClick);
        enterPinCta.classList.add('btn-disabled');
        enterPinCta.setAttribute('aria-disabled', 'true');
        enterPinCta.setAttribute('disabled', 'true');
    }

    if (enterPinInput) {
        enterPinInput.addEventListener('click', handleContainerClick);
    }

    // --- Cleanup ---
    return () => {
        inputs.forEach(input => {
            input.removeEventListener('input', handleInput);
            input.removeEventListener('keydown', handleKeyDown);
            input.removeEventListener('keydown', handleStrictBackspace);
            input.removeEventListener('keydown', handleEnterKey);
            input.removeEventListener('focus', handleFocus);
            input.removeEventListener('mouseup', handleMouseUp);
        });
        if (enterPinCta) {
            enterPinCta.removeEventListener('click', handleButtonClick);
        }
        if (enterPinInput) {
            enterPinInput.removeEventListener('click', handleContainerClick);
        }
    };
};


interface EnterPinInitProps {
    el: HTMLDivElement;
}

const EnterPinInit = ({ el }: EnterPinInitProps): void => {
    EnterPin(el);
};

export default EnterPinInit;
